import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { X, FileText, Trash2, ChevronRight, Calendar, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import PremiumMonthlyReport from './PremiumMonthlyReport';
import { API_URL } from '../config/api';

const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

const SavedReportsHistory = ({ open, onClose, calendarId }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showReport, setShowReport] = useState(false);

  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  useEffect(() => {
    if (open && calendarId) {
      loadReports();
    }
  }, [open, calendarId]);

  const loadReports = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/saved-reports?calendar_id=${calendarId}`,
        { withCredentials: true }
      );
      setReports(response.data);
    } catch (error) {
      console.error('Error loading reports:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (reportId, e) => {
    e.stopPropagation();
    if (!confirm('Supprimer ce bilan ?')) return;
    
    try {
      await axios.delete(`${API}/saved-reports/${reportId}`, { withCredentials: true });
      setReports(reports.filter(r => r.id !== reportId));
      toast.success('Bilan supprimé');
    } catch (error) {
      console.error('Error deleting report:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleViewReport = (report) => {
    setSelectedReport(report);
    setShowReport(true);
  };

  if (!open) return null;

  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
          
          {/* Content */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25 }}
            className="relative w-full sm:max-w-md max-h-[90vh] bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4 sticky top-0 z-10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <BookOpen className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">Historique des bilans</h2>
                    <p className="text-xs text-white/70">{reports.length} bilan(s) sauvegardé(s)</p>
                  </div>
                </div>
                <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 transition-colors">
                  <X className="h-5 w-5 text-white" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="overflow-y-auto" style={{ maxHeight: 'calc(90vh - 100px)' }}>
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <motion.div 
                    className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  />
                </div>
              ) : reports.length === 0 ? (
                <div className="p-8 text-center">
                  <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 mb-2">Aucun bilan sauvegardé</p>
                  <p className="text-sm text-gray-400">
                    Consultez un bilan mensuel et cliquez sur "Sauvegarder" pour le retrouver ici
                  </p>
                </div>
              ) : (
                <div className="p-4 space-y-2">
                  {reports.map((report, index) => (
                    <motion.button
                      key={report.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      onClick={() => handleViewReport(report)}
                      className="w-full bg-gray-50 hover:bg-gray-100 rounded-xl p-4 flex items-center justify-between transition-colors text-left"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-xl flex flex-col items-center justify-center text-white">
                          <span className="text-xs font-medium">{monthNames[report.month - 1].slice(0, 3)}</span>
                          <span className="text-sm font-bold">{report.year}</span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            {monthNames[report.month - 1]} {report.year}
                          </h3>
                          <p className="text-xs text-gray-500">
                            Sauvegardé le {new Date(report.created_at).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => handleDelete(report.id, e)}
                          className="p-2 text-gray-400 hover:text-rose-500 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      </div>
                    </motion.button>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>

      {/* View Saved Report */}
      {showReport && selectedReport && (
        <PremiumMonthlyReport
          open={true}
          onClose={() => {
            setShowReport(false);
            setSelectedReport(null);
          }}
          calendarId={calendarId}
          initialYear={selectedReport.year}
          initialMonth={selectedReport.month}
        />
      )}
    </>
  );
};

export default SavedReportsHistory;
